import type { AdminCompanyStatusChange, AdminCompanyStatusHistory, CompanyStatusScope } from '../../api/types';
import { httpDelete, httpDownload, httpGet, httpGetResponse, httpPost, httpPostForm, httpPut } from '../../api/httpClient';
import {
  AdminBootstrapDictionaries,
  AdminCatalogCategory,
  AdminCatalogCategoryCreateRequest,
  AdminCatalogCategoryListParams,
  AdminCatalogCategoryUpdateRequest,
  AdminCatalogViewListParams,
  AdminCatalogViewProduct,
  AdminCatalogViewResult,
  AdminCatalogProduct,
  AdminCatalogProductCreateRequest,
  AdminCatalogProductImage,
  AdminCatalogProductListParams,
  AdminCatalogProductUpdateRequest,
  AdminCompany,
  AdminCompanyAgreement,
  AdminCompanyChat,
  AdminCompanyFile,
  AdminCompanyFileLinkCreateRequest,
  AdminCompanyDeliveryAddress,
  AdminCompanyDeliveryAddressSaveRequest,
  AdminOrderDetail,
  AdminFileContext,
  AdminStoredFile,
  AdminDeliveryAddressSuggestion,
  AdminCompanyDeliveryZone,
  AdminCompanyDeliveryZonesUpdateRequest,
  AdminCompanyUpdateRequest,
  AdminCompanyUser,
  AdminDeliveryZone,
  AdminDeliveryZoneCreateRequest,
  AdminDeliveryZoneListParams,
  AdminDeliveryZoneUpdateRequest,
  AdminListParams,
  AdminListResult,
  AdminFileRegistryItem,
  AdminFileRegistryListParams,
  AdminQueueDashboard,
  AdminProductOffer,
  AdminProductOfferListParams,
  AdminUser,
  AdminUserUpdateRequest,
  RejectCompanyRequest,
} from '../../api/types';

const DELIVERY_ZONE_PAGE_SIZE = 200;

export const adminApi = {
  async getFileRegistry(params: AdminFileRegistryListParams): Promise<AdminListResult<AdminFileRegistryItem>> {
    const response = await httpGetResponse<AdminFileRegistryItem[]>('/v1/diagnostics/files', createFileRegistryQueryParams(params));

    return {
      items: response.data,
      meta: response.meta,
    };
  },

  getQueueDashboard(): Promise<AdminQueueDashboard> {
    return httpGetResponse<AdminQueueDashboard>('/v1/diagnostics/queues', {}).then((response) => response.data);
  },

  getBootstrapDictionaries(): Promise<AdminBootstrapDictionaries> {
    return httpGetResponse<AdminBootstrapDictionaries>('/v1/dictionary/bootstrap', {}).then((response) => response.data);
  },

  async getUsers(params: AdminListParams): Promise<AdminListResult<AdminUser>> {
    const response = await httpGetResponse<AdminUser[]>('/v1/user', createQueryParams(params));

    return {
      items: response.data,
      meta: response.meta,
    };
  },

  async getCompanies(params: AdminListParams): Promise<AdminListResult<AdminCompany>> {
    const response = await httpGetResponse<AdminCompany[]>('/v1/company', createQueryParams(params));

    return {
      items: response.data,
      meta: response.meta,
    };
  },

  getOrder(orderId: number): Promise<AdminOrderDetail> {
    return httpGet<AdminOrderDetail>(`/v1/orders/${orderId}`);
  },

  async getDeliveryZones(params: AdminDeliveryZoneListParams): Promise<AdminListResult<AdminDeliveryZone>> {
    const response = await httpGetResponse<AdminDeliveryZone[]>('/v1/delivery-zone', createDeliveryZoneQueryParams(params));

    return {
      items: response.data,
      meta: response.meta,
    };
  },

  async getDeliveryZoneCatalog(params: AdminDeliveryZoneListParams, onFirstPage?: (page: AdminListResult<AdminDeliveryZone>) => void): Promise<{ firstPage: AdminListResult<AdminDeliveryZone>; allZones: AdminDeliveryZone[] }> {
    const firstPage = await adminApi.getDeliveryZones({ ...params, page: 1, per_page: DELIVERY_ZONE_PAGE_SIZE });
    onFirstPage?.(firstPage);
    const pageCount = firstPage.meta.page_count ?? 1;
    const allZones = [...firstPage.items];
    for (let page = 2; page <= pageCount; page += 1) {
      const nextPage = await adminApi.getDeliveryZones({ ...params, page, per_page: DELIVERY_ZONE_PAGE_SIZE });
      allZones.push(...nextPage.items);
    }

    return { firstPage, allZones };
  },

  async getCatalogCategories(params: AdminCatalogCategoryListParams): Promise<AdminListResult<AdminCatalogCategory>> {
    const response = await httpGetResponse<AdminCatalogCategory[]>('/v1/catalog-category', createCatalogCategoryQueryParams(params));

    return {
      items: response.data,
      meta: response.meta,
    };
  },

  async getCatalogProducts(params: AdminCatalogProductListParams): Promise<AdminListResult<AdminCatalogProduct>> {
    const response = await httpGetResponse<AdminCatalogProduct[]>('/v1/catalog-product', createCatalogProductQueryParams(params));

    return {
      items: response.data,
      meta: response.meta,
    };
  },

  async getProductOffers(params: AdminProductOfferListParams): Promise<AdminListResult<AdminProductOffer>> {
    const response = await httpGetResponse<AdminProductOffer[]>('/v1/product-offer', createProductOfferQueryParams(params));

    return {
      items: response.data,
      meta: response.meta,
    };
  },

  async getCatalogView(
    params: AdminCatalogViewListParams,
  ): Promise<AdminListResult<AdminCatalogViewProduct> & AdminCatalogViewResult> {
    const response = await httpGetResponse<AdminCatalogViewResult>('/v1/catalog-view', createCatalogViewQueryParams(params));

    return {
      ...response.data,
      meta: response.meta,
    };
  },

  updateUser(userId: number, payload: AdminUserUpdateRequest): Promise<AdminUser> {
    return httpPut<AdminUser, AdminUserUpdateRequest>(`/v1/user/${userId}`, payload);
  },

  changeCompanyStatus(companyId: number, payload: AdminCompanyStatusChange): Promise<AdminCompany> {
    return httpPut<AdminCompany, AdminCompanyStatusChange>(`/v1/company/${companyId}/status`, payload);
  },

  async getCompanyStatusHistory(companyId: number, page: number, statusScope?: CompanyStatusScope): Promise<AdminListResult<AdminCompanyStatusHistory>> {
    const response = await httpGetResponse<AdminCompanyStatusHistory[]>(`/v1/company/${companyId}/status-history`, { page: String(page), per_page: '20', ...(statusScope ? { status_scope: statusScope } : {}) });
    if (!Array.isArray(response.data)) {
      throw new Error('Не удалось загрузить историю статусов.');
    }
    return { items: response.data, meta: response.meta };
  },

  updateCompany(companyId: number, payload: AdminCompanyUpdateRequest): Promise<AdminCompany> {
    return httpPut<AdminCompany, AdminCompanyUpdateRequest>(`/v1/company/${companyId}`, payload);
  },

  async getCompanyUsers(companyId: number, page = 1): Promise<AdminListResult<AdminCompanyUser>> {
    const response = await httpGetResponse<AdminCompanyUser[]>(`/v1/company/${companyId}/users`, {
      page: String(page),
      per_page: '20',
    });

    return {
      items: response.data,
      meta: response.meta,
    };
  },

  getCompanyChats(companyId: number): Promise<AdminCompanyChat[]> {
    return httpGetResponse<AdminCompanyChat[]>(`/v1/company/${companyId}/chats`, {}).then((response) => response.data);
  },

  getCompanyFileContexts(): Promise<AdminFileContext[]> {
    return httpGetResponse<AdminFileContext[]>('/v1/company/file-contexts', {}).then((response) => response.data);
  },

  getCompanyFiles(companyId: number): Promise<AdminCompanyFile[]> {
    return httpGetResponse<AdminCompanyFile[]>(`/v1/company/${companyId}/files`, {}).then((response) => response.data);
  },

  downloadCompanyFile(companyId: number, fileId: number, fileName?: string): Promise<void> {
    return httpDownload(`/v1/company/${companyId}/files/${fileId}/download`, undefined, fileName);
  },

  uploadCompanyFile(file: File): Promise<AdminStoredFile> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_public_name', file.name);

    return httpPostForm<AdminStoredFile>('/v1/company/files/upload', formData);
  },

  linkCompanyFile(
    companyId: number,
    payload: AdminCompanyFileLinkCreateRequest,
  ): Promise<AdminCompanyFile> {
    return httpPost<AdminCompanyFile, AdminCompanyFileLinkCreateRequest>(`/v1/company/${companyId}/files`, payload);
  },

  getCompanyDeliveryAddresses(companyId: number): Promise<AdminCompanyDeliveryAddress[]> {
    return httpGetResponse<AdminCompanyDeliveryAddress[]>(`/v1/company/${companyId}/delivery-addresses`, {}).then((response) => response.data);
  },

  getCompanyDeliveryAddressSuggestions(companyId: number, query: string): Promise<AdminDeliveryAddressSuggestion[]> {
    return httpGet<AdminDeliveryAddressSuggestion[]>(`/v1/company/${companyId}/delivery-addresses/suggestions`, { query });
  },

  createCompanyDeliveryAddress(
    companyId: number,
    payload: AdminCompanyDeliveryAddressSaveRequest,
  ): Promise<AdminCompanyDeliveryAddress> {
    return httpPost<AdminCompanyDeliveryAddress, AdminCompanyDeliveryAddressSaveRequest>(
      `/v1/company/${companyId}/delivery-addresses`,
      payload,
    );
  },

  updateCompanyDeliveryAddress(
    companyId: number,
    addressId: number,
    payload: AdminCompanyDeliveryAddressSaveRequest,
  ): Promise<AdminCompanyDeliveryAddress> {
    return httpPut<AdminCompanyDeliveryAddress, AdminCompanyDeliveryAddressSaveRequest>(
      `/v1/company/${companyId}/delivery-addresses/${addressId}`,
      payload,
    );
  },

  deleteCompanyDeliveryAddress(companyId: number, addressId: number): Promise<void> {
    return httpDelete(`/v1/company/${companyId}/delivery-addresses/${addressId}`);
  },

  getCompanyDeliveryZones(companyId: number): Promise<AdminCompanyDeliveryZone[]> {
    return httpGetResponse<AdminCompanyDeliveryZone[]>(`/v1/company/${companyId}/delivery-zones`, {}).then((response) => response.data);
  },

  updateCompanyDeliveryZones(
    companyId: number,
    payload: AdminCompanyDeliveryZonesUpdateRequest,
  ): Promise<AdminCompanyDeliveryZone[]> {
    return httpPut<AdminCompanyDeliveryZone[], AdminCompanyDeliveryZonesUpdateRequest>(
      `/v1/company/${companyId}/delivery-zones`,
      payload,
    );
  },

  approveCompany(companyId: number): Promise<AdminCompany> {
    return httpPost<AdminCompany>(`/v1/company/${companyId}/approve`);
  },

  signCompanyContract(companyId: number): Promise<AdminCompany> {
    return httpPost<AdminCompany>(`/v1/company/${companyId}/sign-contract`);
  },

  getCompanyAgreement(companyId: number): Promise<AdminCompanyAgreement> {
    return httpGet<AdminCompanyAgreement>(`/v1/company/${companyId}/agreement`);
  },

  rejectCompany(companyId: number, payload: RejectCompanyRequest): Promise<AdminCompany> {
    return httpPost<AdminCompany, RejectCompanyRequest>(`/v1/company/${companyId}/reject`, payload);
  },

  createDeliveryZone(payload: AdminDeliveryZoneCreateRequest): Promise<AdminDeliveryZone> {
    return httpPost<AdminDeliveryZone, AdminDeliveryZoneCreateRequest>('/v1/delivery-zone', payload);
  },

  updateDeliveryZone(zoneId: number, payload: AdminDeliveryZoneUpdateRequest): Promise<AdminDeliveryZone> {
    return httpPut<AdminDeliveryZone, AdminDeliveryZoneUpdateRequest>(`/v1/delivery-zone/${zoneId}`, payload);
  },

  deleteDeliveryZone(zoneId: number): Promise<void> {
    return httpDelete(`/v1/delivery-zone/${zoneId}`);
  },

  createCatalogCategory(payload: AdminCatalogCategoryCreateRequest): Promise<AdminCatalogCategory> {
    return httpPost<AdminCatalogCategory, AdminCatalogCategoryCreateRequest>('/v1/catalog-category', payload);
  },

  updateCatalogCategory(categoryId: number, payload: AdminCatalogCategoryUpdateRequest): Promise<AdminCatalogCategory> {
    return httpPut<AdminCatalogCategory, AdminCatalogCategoryUpdateRequest>(`/v1/catalog-category/${categoryId}`, payload);
  },

  deleteCatalogCategory(categoryId: number): Promise<void> {
    return httpDelete(`/v1/catalog-category/${categoryId}`);
  },

  createCatalogProduct(payload: AdminCatalogProductCreateRequest): Promise<AdminCatalogProduct> {
    return httpPost<AdminCatalogProduct, AdminCatalogProductCreateRequest>('/v1/catalog-product', payload);
  },

  updateCatalogProduct(productId: number, payload: AdminCatalogProductUpdateRequest): Promise<AdminCatalogProduct> {
    return httpPut<AdminCatalogProduct, AdminCatalogProductUpdateRequest>(`/v1/catalog-product/${productId}`, payload);
  },

  deleteCatalogProduct(productId: number): Promise<void> {
    return httpDelete(`/v1/catalog-product/${productId}`);
  },

  getCatalogProductImages(productId: number): Promise<AdminCatalogProductImage[]> {
    return httpGetResponse<AdminCatalogProductImage[]>(`/v1/catalog-product/${productId}/images`, {}).then((response) => response.data);
  },


};

function createQueryParams(params: AdminListParams): Record<string, string> {
  const queryParams: Record<string, string> = {};

  if (params.search !== undefined && params.search !== '') {
    queryParams.search = params.search;
  }

  if (params.user_id !== undefined && params.user_id !== null) {
    queryParams.user_id = String(params.user_id);
  }

  if (params.user_email !== undefined && params.user_email !== '') {
    queryParams.user_email = params.user_email;
  }

  if (params.user_full_name !== undefined && params.user_full_name !== '') {
    queryParams.user_full_name = params.user_full_name;
  }

  if (params.user_phone !== undefined && params.user_phone !== '') {
    queryParams.user_phone = params.user_phone;
  }

  if (params.user_email_confirmed !== undefined && params.user_email_confirmed !== null) {
    queryParams.user_email_confirmed = params.user_email_confirmed ? '1' : '0';
  }

  if (params.company_id !== undefined && params.company_id !== null) {
    queryParams.company_id = String(params.company_id);
  }

  if (params.company_name !== undefined && params.company_name !== '') {
    queryParams.company_name = params.company_name;
  }

  if (params.company_registration_address !== undefined && params.company_registration_address !== '') {
    queryParams.company_registration_address = params.company_registration_address;
  }

  if (params.company_actual_address !== undefined && params.company_actual_address !== '') {
    queryParams.company_actual_address = params.company_actual_address;
  }

  if (params.company_inn !== undefined && params.company_inn !== '') {
    queryParams.company_inn = params.company_inn;
  }

  if (params.company_kpp !== undefined && params.company_kpp !== '') {
    queryParams.company_kpp = params.company_kpp;
  }

  if (params.company_ogrn !== undefined && params.company_ogrn !== '') {
    queryParams.company_ogrn = params.company_ogrn;
  }

  if (params.company_rs !== undefined && params.company_rs !== '') {
    queryParams.company_rs = params.company_rs;
  }

  if (params.company_ks !== undefined && params.company_ks !== '') {
    queryParams.company_ks = params.company_ks;
  }

  if (params.company_bik !== undefined && params.company_bik !== '') {
    queryParams.company_bik = params.company_bik;
  }

  if (params.company_bank_name !== undefined && params.company_bank_name !== '') {
    queryParams.company_bank_name = params.company_bank_name;
  }

  if (params.company_phone !== undefined && params.company_phone !== '') {
    queryParams.company_phone = params.company_phone;
  }

  if (params.company_email !== undefined && params.company_email !== '') {
    queryParams.company_email = params.company_email;
  }

  if (params.company_edo_id !== undefined && params.company_edo_id !== '') {
    queryParams.company_edo_id = params.company_edo_id;
  }

  if (params.company_reject_comment !== undefined && params.company_reject_comment !== '') {
    queryParams.company_reject_comment = params.company_reject_comment;
  }

  if (params.company_created_by !== undefined && params.company_created_by !== null) {
    queryParams.company_created_by = String(params.company_created_by);
  }

  if (params.status_id !== undefined && params.status_id !== null) {
    queryParams.status_id = String(params.status_id);
  }

  if (params.workflow_status !== undefined) {
    queryParams.workflow_status = params.workflow_status;
  }

  if (params.page !== undefined) {
    queryParams.page = String(params.page);
  }

  if (params.per_page !== undefined) {
    queryParams.per_page = String(params.per_page);
  }

  return queryParams;
}

function createDeliveryZoneQueryParams(params: AdminDeliveryZoneListParams): Record<string, string> {
  const queryParams: Record<string, string> = {};

  if (params.include_coords !== undefined) queryParams.include_coords = params.include_coords ? '1' : '0';

  if (params.search !== undefined && params.search !== '') {
    queryParams.search = params.search;
  }

  if (params.zone_id !== undefined && params.zone_id !== null) {
    queryParams.zone_id = String(params.zone_id);
  }

  if (params.zone_region_id !== undefined && params.zone_region_id !== null) {
    queryParams.zone_region_id = String(params.zone_region_id);
  }

  if (params.zone_name !== undefined && params.zone_name !== '') {
    queryParams.zone_name = params.zone_name;
  }

  if (params.page !== undefined) {
    queryParams.page = String(params.page);
  }

  if (params.per_page !== undefined) {
    queryParams.per_page = String(params.per_page);
  }

  return queryParams;
}

function createCatalogCategoryQueryParams(params: AdminCatalogCategoryListParams): Record<string, string> {
  const queryParams: Record<string, string> = {};

  if (params.search !== undefined && params.search !== '') {
    queryParams.search = params.search;
  }

  if (params.category_id !== undefined && params.category_id !== null) {
    queryParams.category_id = String(params.category_id);
  }

  if (params.category_parent_id !== undefined && params.category_parent_id !== null) {
    queryParams.category_parent_id = String(params.category_parent_id);
  }

  if (params.category_name !== undefined && params.category_name !== '') {
    queryParams.category_name = params.category_name;
  }

  if (params.page !== undefined) {
    queryParams.page = String(params.page);
  }

  if (params.per_page !== undefined) {
    queryParams.per_page = String(params.per_page);
  }

  return queryParams;
}

function createCatalogProductQueryParams(params: AdminCatalogProductListParams): Record<string, string> {
  const queryParams: Record<string, string> = {};

  if (params.search !== undefined && params.search !== '') {
    queryParams.search = params.search;
  }

  if (params.product_id !== undefined && params.product_id !== null) {
    queryParams.product_id = String(params.product_id);
  }

  if (params.product_category_id !== undefined && params.product_category_id !== null) {
    queryParams.product_category_id = String(params.product_category_id);
  }

  if (params.product_sku !== undefined && params.product_sku !== '') {
    queryParams.product_sku = params.product_sku;
  }

  if (params.product_name !== undefined && params.product_name !== '') {
    queryParams.product_name = params.product_name;
  }

  if (params.product_unit_id !== undefined && params.product_unit_id !== null) {
    queryParams.product_unit_id = String(params.product_unit_id);
  }

  if (params.product_status_id !== undefined && params.product_status_id !== null) {
    queryParams.product_status_id = String(params.product_status_id);
  }

  if (params.page !== undefined) {
    queryParams.page = String(params.page);
  }

  if (params.per_page !== undefined) {
    queryParams.per_page = String(params.per_page);
  }

  return queryParams;
}

function createProductOfferQueryParams(params: AdminProductOfferListParams): Record<string, string> {
  const queryParams: Record<string, string> = {};

  if (params.search !== undefined && params.search !== '') {
    queryParams.search = params.search;
  }

  if (params.offer_id !== undefined && params.offer_id !== null) {
    queryParams.offer_id = String(params.offer_id);
  }

  if (params.product_id !== undefined && params.product_id !== null) {
    queryParams.product_id = String(params.product_id);
  }

  if (params.company_id !== undefined && params.company_id !== null) {
    queryParams.company_id = String(params.company_id);
  }

  if (params.offer_availability !== undefined && params.offer_availability !== null) {
    queryParams.offer_availability = params.offer_availability ? '1' : '0';
  }

  if (params.page !== undefined) {
    queryParams.page = String(params.page);
  }

  if (params.per_page !== undefined) {
    queryParams.per_page = String(params.per_page);
  }

  return queryParams;
}

function createCatalogViewQueryParams(params: AdminCatalogViewListParams): Record<string, string> {
  const queryParams: Record<string, string> = {
    delivery_address_id: String(params.delivery_address_id),
  };

  if (params.category_id !== undefined && params.category_id !== null) {
    queryParams.category_id = String(params.category_id);
  }

  if (params.search !== undefined && params.search !== '') {
    queryParams.search = params.search;
  }

  if (params.max_price !== undefined && params.max_price !== null) {
    queryParams.max_price = String(params.max_price);
  }

  if (params.max_delivery_days !== undefined && params.max_delivery_days !== null) {
    queryParams.max_delivery_days = String(params.max_delivery_days);
  }

  if (params.sort !== undefined) {
    queryParams.sort = params.sort;
  }

  if (params.include_offers === true) {
    queryParams.include_offers = '1';
  }

  if (params.page !== undefined) {
    queryParams.page = String(params.page);
  }

  if (params.per_page !== undefined) {
    queryParams.per_page = String(params.per_page);
  }

  return queryParams;
}

function createFileRegistryQueryParams(params: AdminFileRegistryListParams): Record<string, string> {
  const queryParams: Record<string, string> = {};

  if (params.search !== undefined && params.search !== '') {
    queryParams.search = params.search;
  }

  if (params.access_policy !== undefined) {
    queryParams.access_policy = params.access_policy;
  }

  if (params.lifecycle !== undefined) {
    queryParams.lifecycle = params.lifecycle;
  }

  if (params.processing_status !== undefined) {
    queryParams.processing_status = params.processing_status;
  }

  if (params.page !== undefined) {
    queryParams.page = String(params.page);
  }

  if (params.per_page !== undefined) {
    queryParams.per_page = String(params.per_page);
  }

  return queryParams;
}
